import { HOME_MSG, INVOICE_STATE_LABEL, PAYMENT_METHOD_LABEL, PAY_SCHEDULE_MSG, overdueText, type GroupDetail, type HomeSummary, type Invoice } from "@genbaryoku/contract";
import { Link, useNavigate } from "react-router";
import { Icon } from "../../components/Shell.jsx";
import type { HeadAction } from "../../components/HeadActions.jsx";
import { Help } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { md, mdSlash, yen } from "../../lib/num.js";

/**
 * 請求書の画面の入金の状態（差し込み口 invoice.head。出した請求書で、API が payments を返したときだけ）。
 * #171（docs/10 §5.12）で帯をやめ、見出しの右のチップとボタンに：
 *   チップ＝「期限超過 MM/DD」（朱。日数は title）・「一部入金」（藍）・「未入金」・「入金済み」（藍）・「残 ○円」。色だけで伝えない（文字で言う）。
 *   #172：朱は期限超過のときだけ。期限内の一部入金は藍、期限を過ぎた一部入金は「期限超過 MM/DD」（朱）を先に、「一部入金」（藍）を並べる
 *   ボタン＝「入金 n件」（入金の行の一覧＝日・方法・額。押すとその入金へ）・「入金記録」。
 *   #172：ボタンは描かずに invoicePaymentActions が項目で返す（差し込み口 registerActions）。請求書の画面が自分の操作と合わせて
 *   主なボタン1つ（残があれば「入金記録」）と「⋯」（「入金 n件 ›」→ 入金の行）にする＝見出しを 1280px でも1段に
 */
export function InvoicePaymentState({ invoice }: { invoice: Invoice }) {
  const p = invoice.payments;
  if (!p) return null;
  // 分割払い（#267 3b）：期限は残のある最初の回の期日
  const due = p.nextDueDate ?? invoice.revision.dueDate;
  const partial = p.state === "partial" || (p.state === "overdue" && p.rows.length > 0);
  return (
    <>
      {p.state === "overdue" ? (
        <span className="chip shu" data-testid="payment-state" title={`お支払期限 ${md(due)}・${overdueText(p.overdueDays)}`}>
          期限超過{due ? ` ${mdSlash(due)}` : ""}
        </span>
      ) : (
        <span className={"chip" + (p.state === "paid" || p.state === "partial" ? " ai" : " quiet")} data-testid="payment-state">
          {INVOICE_STATE_LABEL[p.state]}
        </span>
      )}
      {p.state === "overdue" && partial ? (
        <span className="chip ai" data-testid="payment-partial">
          {INVOICE_STATE_LABEL.partial}
        </span>
      ) : null}
      {p.balance > 0 ? (
        <span className="chip quiet" data-testid="payment-balance" title={`請求額 ${yen(p.total)} 円`}>
          残額 <span className="num">{yen(p.balance)}</span>円
        </span>
      ) : null}
    </>
  );
}

/** 請求書の見出しの右の入金の操作（#172）：残があれば「入金記録」（主なボタンの候補の1番）、入金があれば「入金 n件 ›」（⋯ の中・押すとその入金へ） */
export function invoicePaymentActions({ invoice }: { invoice: Invoice }): HeadAction[] {
  const p = invoice.payments;
  if (!p) return [];
  return [
    ...(p.balance > 0 ? [{ label: "入金記録", to: `/payments/new?invoice=${invoice.id}`, testId: "record-payment", rank: 10 }] : []),
    ...(p.rows.length
      ? [
          {
            label: `入金 ${p.rows.length}件`,
            testId: "payment-rows",
            sub: p.rows.map((r) => ({
              label: `${md(r.receivedOn)} ${PAYMENT_METHOD_LABEL[r.method]} ${yen(r.amount)} 円${r.fee ? `（手数料 ${yen(r.fee)} 円）` : ""}${r.discount ? `（値引き ${yen(r.discount)} 円）` : ""}`,
              to: `/payments/${r.paymentId}`,
              testId: "payment-row",
            })),
          },
        ]
      : []),
  ];
}

/**
 * ホームのカード「入金待ち」（差し込み口 home.cards。営業力 docs/08 決めごと13）。
 * 件数と合計、上から5行（期限超過が上・日数つき）、行の「入金登録」。まだ消し込んでいない入金があれば1行（営業力ルール9）。
 * 9/29（#91、home.html）：見出しの右に 件数・合計。
 * 10/21（#226、Figma「ホームの圧縮」①改）：白のカード・行は高さ 52px で上に罫線1本＝期限のチップ「期限 10月31日」（期限超過は朱の「期限超過 n日」）・
 *   {customer}名 15px 太字・番号のチップ・件名（1行で「…」）・右に「残額 ○円」・枠のボタン「入金登録」36px。文は contract の HOME_MSG
 */
export function AwaitingPaymentCard({ home }: { home: HomeSummary }) {
  const a = home.awaitingPayment;
  useLabels();
  const nav = useNavigate();
  if (!a) return null;
  return (
    <section className={"card awaitpay" + (a.count ? "" : " zero")} data-testid="home-awaiting-payment">
      <div className="aphead">
        <h3 className="hc-title">
          <span className="hc-mark" aria-hidden="true">
            <Icon k="yen" />
          </span>
          <span className="hc-tt">{HOME_MSG.awaiting}</span>
          <Help testId="help-awaiting" text={t(HOME_MSG.helpAwaiting)} />
        </h3>
        <span className="sp" />
        <span className="apsum" data-testid={`card-${HOME_MSG.awaiting}`}>
          <b className="num">{a.count}</b>件
          {a.count ? (
            <>
              <span className="apgap">{HOME_MSG.total}</span>
              <b className="num">{yen(a.total)}</b>円
              {a.overdueCount ? <span className="shu apover">{HOME_MSG.overdueSum} <b className="num">{a.overdueCount}</b> 件 <span className="num">{yen(a.overdueTotal)}</span> 円</span> : null}
            </>
          ) : null}
        </span>
      </div>
      {a.rows.length ? (
        <div className="aprows">
          {a.rows.map((r) => (
            <div key={`${r.documentId}-${r.seq}`} className="aprow" data-testid="awaiting-row" onClick={() => nav(`/invoices/${r.documentId}`)}>
              {r.state === "overdue" ? (
                <span className="pill shu apdue" data-testid="awaiting-overdue">
                  <Icon k="cal" />
                  {overdueText(r.overdueDays)}
                </span>
              ) : (
                <span className="pill quiet apdue num">
                  <Icon k="cal" />
                  {r.dueDate ? HOME_MSG.due(md(r.dueDate)) : HOME_MSG.noDue}
                </span>
              )}
              <span className="apname">
                <Link to={`/invoices/${r.documentId}`} onClick={(e) => e.stopPropagation()} title={r.clientName}>
                  {r.clientName}
                </Link>
              </span>
              <span className="tag">{r.documentNo}</span>
              {/* 分割払いの回（#267 3b：行は回ごと） */}
              {r.ofCount > 1 ? (
                <span className="tag" data-testid="awaiting-seq">{PAY_SCHEDULE_MSG.seqOf(r.seq, r.ofCount)}</span>
              ) : null}
              <span className="apsubj" title={r.subject}>{r.subject}</span>
              <span className="apbal">
                <span className="u">{HOME_MSG.balance}</span> <b className="num">{yen(r.balance)}</b> <span className="u">円</span>
              </span>
              <Link className="btn aprec" to={`/payments/new?invoice=${r.documentId}`} onClick={(e) => e.stopPropagation()} data-testid="awaiting-record">
                <Icon k="check" />
                {HOME_MSG.record}
              </Link>
            </div>
          ))}
        </div>
      ) : null}
      {(a.rowCount ?? a.count) > a.rows.length || a.unallocatedCount ? (
        <div className="apmore">
          {(a.rowCount ?? a.count) > a.rows.length ? <Link className="btn text" to="/invoices?tab=unpaid">{HOME_MSG.awaitingAll(a.count)}</Link> : null}
          {a.unallocatedCount ? (
            <Link className="btn text" to="/payments" data-testid="home-unallocated">
              未消込入金 {a.unallocatedCount}件（<span className="num">{yen(a.unallocatedTotal)}</span> 円）
            </Link>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** 案件の画面の「入金」タブ（差し込み口 group.payments）。この案件の請求書に当てた入金 */
export function GroupPaymentsPanel({ group }: { group: GroupDetail }) {
  const L = useLabels();
  const nav = useNavigate();
  const rows = group.payments ?? [];
  return (
    <div role="tabpanel" id="gpanel-payments" aria-labelledby="gtab-payments" className="gpanel">
      {rows.length === 0 ? (
        <div className="gempty">
          <p style={{ margin: 0 }}>この{L.project}の請求書に消込した入金はありません。</p>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="list" data-testid="group-payments">
            <thead>
              <tr>
                <th>入金日</th>
                <th>請求書</th>
                <th>方法</th>
                <th className="amt">消込額</th>
                <th className="amt">手数料・値引き</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.paymentId}-${r.documentId}`} className="r" tabIndex={0} onClick={() => nav(`/payments/${r.paymentId}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/payments/${r.paymentId}`)} data-testid="group-payment-row">
                  <td className="num">{md(r.receivedOn)}</td>
                  <td className="num">{r.documentNo}</td>
                  <td>{PAYMENT_METHOD_LABEL[r.method]}</td>
                  <td className="amt num">{yen(r.amount)} 円</td>
                  <td className="amt num">{r.fee || r.discount ? `${r.fee ? `手数料 ${yen(r.fee)}` : ""}${r.fee && r.discount ? "・" : ""}${r.discount ? `値引き ${yen(r.discount)}` : ""} 円` : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
